import sprintf from 'sprintf';
import ger from './ger';
import eng from './eng';

const languages = {
    eng: eng,
    ger: ger,
};

class I18NUtils {
    constructor(lang) {
        this.lang = lang;
    }

    /**
     * wraps the format string functionality from https://github.com/alexei/sprintf.js
     * Use like this in a translated component:
     * <code>{this.props.strings._utils.sprintf("Format%s %d", 'string', 42)}</code>
     *
     * copied from their README:
     *
     * The placeholders in the format string are marked by `%` and are followed by one or more of these elements, in this order:
     *  <ul>
     *    <li>An optional number followed by a `$` sign that selects which argument index to use for the value. If not specified, arguments will be placed in the same order as the placeholders in the input string.</li>
     *    <li>An optional `+` sign that forces to preceed the result with a plus or minus sign on numeric values. By default, only the `-` sign is used on negative numbers.</li>
     *    <li>An optional padding specifier that says what character to use for padding (if specified). Possible values are `0` or any other character precedeed by a `'` (single quote). The default is to pad with *spaces*.</li>
     *    <li>An optional `-` sign, that causes sprintf to left-align the result of this placeholder. The default is to right-align the result.</li>
     *    <li>An optional number, that says how many characters the result should have. If the value to be returned is shorter than this number, the result will be padded. When used with the `j` (JSON) type specifier, the padding length specifies the tab size used for indentation.</li>
     *    <li>An optional precision modifier, consisting of a `.` (dot) followed by a number, that says how many digits should be displayed for floating point numbers. When used with the `g` type specifier, it specifies the number of significant digits. When used on a string, it causes the result to be truncated.</li>
     *    <li>A type specifier that can be any of:
     *      <ul>
     *        <li><code>%</code> — yields a literal <code>%</code> character</li>
     *        <li><code>b</code> — yields an integer as a binary number</li>
     *        <li><code>c</code> — yields an integer as the character with that ASCII value</li>
     *        <li><code>d</code> or <code>i</code> — yields an integer as a signed decimal number</li>
     *        <li><code>e</code> — yields a float using scientific notation</li>
     *        <li><code>u</code> — yields an integer as an unsigned decimal number</li>
     *        <li><code>f</code> — yields a float as is; see notes on precision above</li>
     *        <li><code>g</code> — yields a float as is; see notes on precision above</li>
     *        <li><code>o</code> — yields an integer as an octal number</li>
     *        <li><code>s</code> — yields a string as is</li>
     *        <li><code>t</code> — yields <code>true</code> or <code>false</code></li>
     *        <li><code>T</code> — yields the type of the argument<sup><a href="#fn-1" name="user-content-fn-ref-1">1</a></sup></li>
     *        <li><code>v</code> — yields the primitive value of the specified argument</li>
     *        <li><code>x</code> — yields an integer as a hexadecimal number (lower-case)</li>
     *        <li><code>X</code> — yields an integer as a hexadecimal number (upper-case)</li>
     *        <li><code>j</code> — yields a JavaScript object or array as a JSON encoded string</li>
     *      </ul>
     *    </li>
     *  </ul>
     *
     * @param {string} formatString
     * @param {Object[]} args
     */
    sprintf(formatString, ...args) {
        if (formatString) {
            return sprintf.vsprintf(formatString, args);
        } else {
            throw new ReferenceError('no format string given!');
        }
    }

    formatNumber(number, digits) {
        if (number === undefined || number === null) {
            return '';
        }
        const minimumFractionDigits = digits === undefined ? 2 : digits;
        const priceFormat = this._getStringsForKey('PriceFormat');
        const language = priceFormat.language;

        return Number(number).toLocaleString(language, {
            minimumFractionDigits,
        });
    }

    /**
     * Format a price with currency according to language settings and options in string mappings.
     * Use like this in a translated component:
     * <code>{this.props.strings._utils.formatPrice({amount:3.50, currency:'YEN'})}</code>
     *
     * @param priceObject - Objext containing the price to display with
     * @param {number} priceObject.amount - the amount of money, with
     * @param {string} priceObject.currency - the given currency. will be mapped to a symbol as defined in messages.
     */
    formatPrice(priceObject) {
        if (!priceObject) {
            return null;
        }
        const priceFormat = this._getStringsForKey('PriceFormat');
        const language = priceFormat.language;
        const amount = priceObject.amount;
        const currency = priceObject.currency;

        if (typeof amount === 'string' || typeof amount === 'number') {
            return Number(amount).toLocaleString(language, {
                style: 'currency',
                currency,
                minimumFractionDigits: 2,
            });
        }
        return amount;
    }

    getCurrencySymbol(currency) {
        const priceFormat = this._getStringsForKey('PriceFormat');
        if (priceFormat.currencySymbol.hasOwnProperty(currency)) {
            return priceFormat.currencySymbol[currency];
        }
        return currency;
    }

    getCurrencyCodeFromAccountingArea(accountingArea) {
        if (!accountingArea) {
            return '';
        }
        this._getStringsForKey('PriceFormat');
        return accountingArea.currency_code || 'EUR';
    }

    getCurrencySymbolFromAccountingArea(accountingArea) {
        return this.getCurrencySymbol(
            this.getCurrencyCodeFromAccountingArea(accountingArea)
        );
    }

    _getStringsForKey(key) {
        return languages[this.lang][key];
    }

    static handleOverflow(label) {
        return label.length >= 15 ? `${label.substr(0, 12)}...` : label;
    }
}

export default I18NUtils;
